import React from 'react';
import { Card, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  AnalyticsWeeklyScenario,
  WeeklyRosterScenarioAnalysis,
} from '../types/analyticsScenario';
import './WeeklyDecisionAnalysis.css';

const { Title, Paragraph, Text } = Typography;

const signed = (value: number, decimals = 1) =>
  `${value > 0 ? '+' : ''}${value.toFixed(decimals)}`;

const resultColor = (result: string) => {
  if (result === 'W') return 'green';
  if (result === 'L') return 'volcano';
  return 'blue';
};

interface WeeklyDecisionAnalysisProps {
  analysis: WeeklyRosterScenarioAnalysis;
}

const WeeklyDecisionAnalysis: React.FC<WeeklyDecisionAnalysisProps> = ({ analysis }) => {
  const columns: ColumnsType<AnalyticsWeeklyScenario> = [
    {
      title: 'Week',
      key: 'week',
      fixed: 'left',
      width: 92,
      render: (_, row) => <strong>{row.year} · {row.week}</strong>,
    },
    {
      title: 'Official',
      key: 'official',
      width: 115,
      render: (_, row) => (
        <span>{row.officialActualPoints.toFixed(1)} <Tag color={resultColor(row.actualResult)}>{row.actualResult}</Tag></span>
      ),
    },
    {
      title: 'Opponent',
      dataIndex: 'opponentPoints',
      key: 'opponentPoints',
      width: 92,
      render: value => value.toFixed(1),
    },
    {
      title: 'Policy lineup',
      key: 'actualRosterPolicyResult',
      width: 126,
      render: (_, row) => (
        <span>{row.actualRosterScenarioPoints.toFixed(1)} <Tag color={resultColor(row.actualRosterPolicyResult)}>{row.actualRosterPolicyResult}</Tag></span>
      ),
    },
    {
      title: 'Draft core',
      key: 'draftCorePolicyResult',
      width: 122,
      render: (_, row) => (
        <span>{row.draftCoreScenarioPoints.toFixed(1)} <Tag color={resultColor(row.draftCorePolicyResult)}>{row.draftCorePolicyResult}</Tag></span>
      ),
    },
    {
      title: 'Lineup Δ',
      dataIndex: 'lineupPointOpportunity',
      key: 'lineupPointOpportunity',
      width: 95,
      render: value => (
        <span className={value > 0 ? 'analytics-negative-number' : 'analytics-positive-number'}>
          {signed(value)}
        </span>
      ),
    },
    {
      title: 'Roster Δ',
      dataIndex: 'transactionRosterPointValue',
      key: 'transactionRosterPointValue',
      width: 95,
      render: value => (
        <span className={value < 0 ? 'analytics-negative-number' : 'analytics-positive-number'}>
          {signed(value)}
        </span>
      ),
    },
    {
      title: 'Interpretation',
      dataIndex: 'decisionNote',
      key: 'decisionNote',
      width: 310,
    },
  ];

  const consequentialWeeks = [...analysis.weeks]
    .sort((left, right) => (
      Math.max(Math.abs(right.lineupPointOpportunity), Math.abs(right.transactionRosterPointValue))
      - Math.max(Math.abs(left.lineupPointOpportunity), Math.abs(left.transactionRosterPointValue))
    ))
    .slice(0, 15);

  return (
    <section className="analytics-section analytics-decision-section">
      <div className="analytics-section-heading">
        <div>
          <Title level={2}>Weekly decision audit</Title>
          <Paragraph>
            A pregame-only replay separates start/sit selection from the value of the roster built
            through trades and waivers. It scores the selected lineup afterward, so this is not best ball.
          </Paragraph>
        </div>
        <Tag color="purple">{analysis.confidence} confidence</Tag>
      </div>

      <div className="analytics-metric-grid analytics-decision-metrics">
        <Card className={`analytics-metric analytics-metric--${analysis.policyLineupWinOpportunity > 0.5 ? 'negative' : 'positive'}`} bordered={false}>
          <Text className="analytics-metric-label">Policy lineup vs actual</Text>
          <div className="analytics-metric-value">{signed(analysis.policyLineupWinOpportunity)} wins</div>
          <Text className="analytics-metric-detail">
            {signed(analysis.policyLineupPointOpportunity)} offensive points; positive means modeled opportunity left unused
          </Text>
        </Card>
        <Card className={`analytics-metric analytics-metric--${analysis.transactionRosterWinValue >= 0 ? 'positive' : 'negative'}`} bordered={false}>
          <Text className="analytics-metric-label">Transactions vs draft core</Text>
          <div className="analytics-metric-value">{signed(analysis.transactionRosterWinValue)} wins</div>
          <Text className="analytics-metric-detail">
            {signed(analysis.transactionRosterPointValue)} offensive points from the transaction-shaped roster
          </Text>
        </Card>
        <Card className="analytics-metric analytics-metric--neutral" bordered={false}>
          <Text className="analytics-metric-label">Reasonable choices retained</Text>
          <div className="analytics-metric-value">{analysis.closeChoicesPreserved}</div>
          <Text className="analytics-metric-detail">
            Actual choices kept within {analysis.closeChoiceTolerancePoints.toFixed(1)} projected points of the cutoff
          </Text>
        </Card>
        <Card className="analytics-metric analytics-metric--neutral" bordered={false}>
          <Text className="analytics-metric-label">Analysis coverage</Text>
          <div className="analytics-metric-value">{analysis.weeksAnalyzed} weeks</div>
          <Text className="analytics-metric-detail">
            {analysis.weeksExcludedForDataQuality} incomplete stat weeks excluded; {analysis.availabilityCoveragePercentage.toFixed(1)}% explicit availability coverage
          </Text>
        </Card>
      </div>

      <div className="analytics-scenario-grid" aria-label="Counterfactual scenarios">
        {analysis.scenarios.map(scenario => (
          <Card key={scenario.key} className="analytics-scenario-card" size="small">
            <Text className="analytics-category">{scenario.label}</Text>
            <div className="analytics-scenario-record">
              {scenario.wins}-{scenario.losses}{scenario.ties ? `-${scenario.ties}` : ''}
            </div>
            <div className="analytics-scenario-deltas">
              <span>{signed(scenario.pointsDeltaVsActual)} points</span>
              <span>{signed(scenario.winDeltaVsActual)} wins</span>
            </div>
            <Paragraph>{scenario.description}</Paragraph>
          </Card>
        ))}
      </div>

      <div className="analytics-weekly-heading">
        <Title level={3}>Most consequential modeled weeks</Title>
        <Paragraph>
          Lineup Δ compares the pregame policy with the recorded starters on the same roster.
          Roster Δ compares that policy roster with the retained draft core.
        </Paragraph>
      </div>
      <Table
        className="analytics-table"
        rowKey={row => `${row.year}-${row.week}`}
        columns={columns}
        dataSource={consequentialWeeks}
        pagination={false}
        scroll={{ x: 1240 }}
        size="middle"
      />
    </section>
  );
};

export default WeeklyDecisionAnalysis;
